import { BaseAnalyticsProvider } from './BaseAnalyticsProvider'
import Meta from './providers/Meta/Meta'
import Mixpanel from './providers/Mixpanel/Mixpanel'

import { AnalyticsProvider } from './AnalyticsManager.types'
import type { ActionName, ProviderConfiguration, Configuration, Action } from './AnalyticsManager.types'
import type { DependencyType } from './AnalyticsManager.dependencies'
import { createUrl } from 'utils/utils'

export class AnalyticsManager extends BaseAnalyticsProvider {
	private analyticsProviders: BaseAnalyticsProvider[] | null = null

	private eventsQueue: { actionName: ActionName; actionData: Action | null }[] = []

	constructor(tenantId: string, host: string, dependencies: DependencyType) {
		super()

		dependencies
			.sendRequest(false, createUrl(host, `/v1/tenants/${tenantId}/eventDrivenAnalytics/configuration`), 'get', null)
			.then((configuration: unknown) => {
				this.analyticsProviders = (configuration as Configuration).providers
					.map((providerConfiguration: ProviderConfiguration) => {
						switch (providerConfiguration.name) {
							case AnalyticsProvider.MIXPANEL:
								return new Mixpanel(providerConfiguration, {
									sendEvent: dependencies.sendEvent,
									isMobileApp: dependencies.isMobileApp,
								}) as BaseAnalyticsProvider
							case AnalyticsProvider.META:
								return new Meta(providerConfiguration, {
									sendEvent: dependencies.sendEvent,
									isMobileApp: dependencies.isMobileApp,
								}) as BaseAnalyticsProvider
							default:
								console.error(`Unknown provider: ${providerConfiguration.name}`)
								return null
						}
					})
					.filter((provider): provider is BaseAnalyticsProvider => provider !== null)

				while (this.eventsQueue.length) {
					const { actionName, actionData } = this.eventsQueue.shift()!
					this.sendEvent(actionName, actionData)
				}
			})
	}

	async sendEvent(actionName: ActionName, actionData: Action | null): Promise<void> {
		if (this.analyticsProviders) {
			await Promise.all(this.analyticsProviders.map((provider) => provider.sendEvent(actionName, actionData)))
		} else {
			this.eventsQueue.push({ actionName, actionData })
		}
	}

	async identifyUser(userId: string): Promise<void> {
		if (this.analyticsProviders && userId) {
			await Promise.all(this.analyticsProviders.map((provider) => provider.identifyUser(userId)))
		}
	}

	async resetUser(): Promise<void> {
		if (this.analyticsProviders) {
			await Promise.all(this.analyticsProviders.map((provider) => provider.resetUser()))
		}
	}
}
